import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Avatar,
  Button,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { getSecureImageUrl } from '../../utils/imageUtils';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

/**
 * Generic list view component for collection/wishlist items
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of items to display
 * @param {function} props.onEdit - Function to handle edit button click
 * @param {function} props.onDelete - Function to handle delete button click
 * @param {string} props.itemType - Type of item ('game', 'console', 'lego')
 * @param {boolean} props.isWishlist - Whether this is a wishlist view
 * @param {function} props.onMoveToCollection - Function to handle moving item to collection (for wishlists)
 * @param {Object} props.renderProps - Custom render props for item-specific fields
 * @returns {JSX.Element} List view component
 */
const ListView = ({
  items,
  onEdit,
  onDelete,
  itemType = 'item',
  isWishlist = false,
  onMoveToCollection,
  onToggleBacklog,
  onToggleDNF,
  renderProps = {}
}) => {
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();
  // Default render functions
  const defaultRenderProps = {
    getImageUrl: (item) => {
      if (itemType === 'console') {
        return item.platformLogo || item.imageUrl;
      }
      return item.imageUrl || item.set_img_url;
    },
    getItemName: (item) => item.name,
    getItemSubtitle: (item) => {
      if (itemType === 'game') return `Platform: ${item.platform} • Condition: ${item.condition || 'N/A'}`;
      if (itemType === 'console') return `Condition: ${item.condition || 'N/A'} • Generation: ${item.generation || 'N/A'}`;
      if (itemType === 'lego') {
        const setNum = item.setNum || item.set_num;
        return `Set #: ${setNum ? setNum.split('-')[0] : 'N/A'} • Condition: ${item.condition || 'N/A'}`;
      }
      if (itemType === 'movie') return `Director: ${item.director || 'N/A'} • Condition: ${item.condition || 'N/A'}`;
      if (itemType === 'music') return `Artist: ${item.artist || 'N/A'} • Condition: ${item.condition || 'N/A'}`;
      if (itemType === 'book') return `${item.author || 'Unknown Author'} • ${item.format || 'Paperback'}${item.series ? ` • ${item.series}` : ''}`;
      if (itemType === 'board-game') {
        const players = item.minPlayers && item.maxPlayers 
          ? (item.minPlayers === item.maxPlayers ? `${item.minPlayers} players` : `${item.minPlayers}-${item.maxPlayers} players`)
          : '';
        return `${players}${players && item.playingTime ? ' • ' : ''}${item.playingTime ? `${item.playingTime} min` : ''} • Condition: ${item.condition || 'N/A'}`;
      }
      return '';
    },
    getItemDetails: (item) => {
      if (itemType === 'game') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Tooltip title={item.played ? 'Played' : 'Not Played'}>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <Typography variant="body2" sx={{ mr: 0.5 }}>Played:</Typography>
                {item.played ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <CancelIcon color="error" fontSize="small" />
                )}
              </Box>
            </Tooltip>
            <Tooltip title={item.completed ? 'Completed' : 'Not Completed'}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 0.5 }}>Completed:</Typography>
                {item.completed ? (
                  <EmojiEventsIcon sx={{ color: 'gold' }} fontSize="small" />
                ) : (
                  <CancelIcon color="error" fontSize="small" />
                )}
              </Box>
            </Tooltip>
          </Box>
        );
      }
      return null;
    },
    getItemDetailUrl: (item) => {
      if (itemType === 'game') return `/item/game/${item._id}`;
      if (itemType === 'console') return `/item/console/${item._id}`;
      if (itemType === 'lego') return `/item/lego/${item._id}`;
      if (itemType === 'movie') return `/item/movie/${item._id}`;
      if (itemType === 'music') return `/item/music/${item._id}`;
      if (itemType === 'book') return `/item/book/${item._id}`;
      if (itemType === 'board-game') return `/item/board-game/${item._id}`;
      return '#';
    }
  };

  // Merge default render props with custom render props
  const mergedRenderProps = { ...defaultRenderProps, ...renderProps };

  return (
    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid item xs={12} key={item._id}>
          <Card 
            sx={{ 
              display: 'flex', 
              height: '100%', 
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            }}
            onClick={(e) => {
              // Only navigate if the click wasn't on a button or icon
              if (!e.target.closest('button')) {
                // Add isWishlistItem property to the item object itself
                const itemWithWishlistFlag = {
                  ...item,
                  isWishlistItem: isWishlist
                };
                navigate(mergedRenderProps.getItemDetailUrl(item), { 
                  state: { 
                    item: itemWithWishlistFlag, 
                    fromWishlist: isWishlist 
                  } 
                });
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <CardContent sx={{ flex: '1 0 auto' }}>
                <Typography 
                  component="div" 
                  variant="h6"
                >
                  {mergedRenderProps.getItemName(item)}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" component="div">
                  {mergedRenderProps.getItemSubtitle(item)}
                </Typography>
                {mergedRenderProps.getItemDetails(item)}
              </CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 1, pb: 1 }}>
                <Box>
                  {onToggleBacklog && !isWishlist && (
                    <Tooltip title={item.inBacklog ? 'Remove from Backlog' : 'Add to Backlog'}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleBacklog(item); }}>
                        {item.inBacklog
                          ? <BookmarkAddedIcon fontSize="small" sx={{ color: '#ce93d8' }} />
                          : <BookmarkAddIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                  {onToggleDNF && !isWishlist && featureFlags.enableDNF === true && (
                    <Tooltip title={item.isDNF ? 'Clear DNF' : 'Mark as DNF'}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleDNF(item); }}>
                        <DoNotDisturbOnIcon fontSize="small" sx={{ color: item.isDNF ? 'warning.main' : 'text.disabled' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton onClick={(e) => {
                    e.stopPropagation();
                    onEdit(item);
                  }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item);
                  }} color="error">
                    <DeleteIcon />
                  </IconButton>
                  {isWishlist && onMoveToCollection && (
                    <IconButton onClick={(e) => {
                      e.stopPropagation();
                      onMoveToCollection(item);
                    }} color="primary">
                      <AddShoppingCartIcon />
                    </IconButton>
                  )}
                </Box>
                {mergedRenderProps.getImageUrl(item) && (
                  <Avatar
                    variant="rounded"
                    sx={{ width: 50, height: 50, mr: 2 }}
                    src={getSecureImageUrl(mergedRenderProps.getImageUrl(item))}
                    alt={mergedRenderProps.getItemName(item)}
                  />
                )}
              </Box>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default ListView;
